import { supabase } from "@/integrations/supabase/client";

// Team member tracking function
async function trackTeamMemberCount(organizationId: string): Promise<void> {
  try {
    if (!organizationId) {
      console.warn("[Team Member Tracking] No organization ID provided");
      return;
    }

    // Get current count of team members for this organization
    const { data: teamMembers, error } = await supabase
      .from("profiles")
      .select("id")
      .eq("organization_id", organizationId)
      .not("joined_at", "is", null); // Only count members who have actually joined

    if (error) {
      console.warn(
        "[Team Member Tracking] Failed to query team members:",
        error.message,
      );
      return;
    }

    const memberCount = teamMembers?.length || 0;

    // Update the organization's team members count directly
    const { error: updateError } = await supabase
      .from("organizations")
      .update({ team_members_count: memberCount })
      .eq("id", organizationId);

    if (updateError) {
      console.warn(
        "[Team Member Tracking] Failed to update team members count:",
        updateError.message,
      );
      return;
    }

    console.log(
      `[Team Member Tracking] ✅ Updated team members count to ${memberCount} for org ${organizationId}`,
    );
  } catch (error) {
    console.warn("[Team Member Tracking] Error:", error);
  }
}

export interface TeamInvitation {
  id: string;
  organization_id: string;
  invited_by: string;
  email: string;
  status: string; // Can be "pending" | "accepted" | "expired" | "cancelled" but DB returns string
  token: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  accepted_at?: string | null;
  accepted_by?: string | null;
}

export interface TeamMember {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  avatar_url: string | null;
  joined_at: string | null;
  created_at: string | null;
}

export const teamService = {
  // Generate a secure random token
  generateInviteToken(): string {
    return crypto.randomUUID();
  },

  // Invite a team member using Edge Function with Supabase Auth
  async inviteTeamMember(
    organizationId: string,
    organizationName: string,
    email: string,
    inviterName: string,
    inviterEmail: string,
    userId: string,
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "invite-team-member",
        {
          body: {
            email: email,
            organizationId: organizationId,
            organizationName: organizationName,
            inviterName: inviterName,
            inviterEmail: inviterEmail,
            userId: userId,
            siteUrl: window.location.origin,
          },
        },
      );

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Failed to invite team member:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Get invitation by token (public access)
  async getInvitationByToken(token: string): Promise<{
    data: (TeamInvitation & { organization: { name: string } }) | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("team_invitations")
        .select(
          `
          *,
          organization:organizations(name)
        `,
        )
        .eq("token", token)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("Invalid or expired invitation"),
      };
    }
  },

  // Accept invitation using Edge Function
  async acceptInvitation(
    token: string,
    organizationId: string,
  ): Promise<{ data: boolean | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "accept-team-invitation",
        {
          body: {
            token: token,
            organizationId: organizationId,
          },
        },
      );

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error("Failed to accept invitation:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("Failed to accept invitation"),
      };
    }
  },

  // Get team members
  async getTeamMembers(organizationId: string): Promise<{
    data: TeamMember[] | null;
    error: Error | null;
  }> {
    try {
      // Use Edge Function for secure server-side fetch (bypasses RLS safely)
      const { data, error } = await supabase.functions.invoke(
        "get-team-members",
        {
          body: {
            organizationId,
            user: { id: (await supabase.auth.getUser()).data.user?.id },
          },
        },
      );

      if (error) throw error;

      return { data: (data?.members as TeamMember[]) || [], error: null };
    } catch (error) {
      console.error("Failed to get team members:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Remove team member
  async removeTeamMember(
    memberId: string,
  ): Promise<{ data: boolean | null; error: Error | null }> {
    try {
      // Get the organization ID before removing the member
      const { data: memberProfile, error: getError } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", memberId)
        .single();

      if (getError) throw getError;

      const organizationId = memberProfile?.organization_id;

      // Remove their organization association
      const { error } = await supabase
        .from("profiles")
        .update({
          organization_id: null,
          joined_at: null,
        })
        .eq("id", memberId);

      if (error) throw error;

      // Track team member count after successful removal
      if (organizationId) {
        trackTeamMemberCount(organizationId).catch((error) =>
          console.warn("Failed to track team member count:", error),
        );
      }

      return { data: true, error: null };
    } catch (error) {
      console.error("Failed to remove team member:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Get pending invitations
  async getPendingInvitations(organizationId: string): Promise<{
    data: TeamInvitation[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("team_invitations")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Failed to get pending invitations:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Cancel invitation
  async cancelInvitation(
    invitationId: string,
    deleteAuthUser: boolean = false,
  ): Promise<{
    data: boolean | null;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase.functions.invoke(
        "cancel-team-invitation",
        {
          body: {
            invitationId: invitationId,
            deleteAuthUser: deleteAuthUser,
          },
        },
      );

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error("Failed to cancel invitation:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Resend invitation
  async resendInvitation(
    invitationId: string,
    organizationId: string,
    organizationName: string,
    email: string,
    inviterName: string,
    inviterEmail: string,
    userId: string,
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      // First cancel the old invitation
      const { error: cancelError } = await supabase
        .from("team_invitations")
        .update({
          status: "cancelled",
          updated_at: new Date().toISOString(),
        })
        .eq("id", invitationId);

      if (cancelError) {
        throw cancelError;
      }

      // Send a new invitation using the same edge function
      // This will create a new invitation with a fresh token from Supabase Auth
      const result = await this.inviteTeamMember(
        organizationId,
        organizationName,
        email,
        inviterName,
        inviterEmail,
        userId,
      );

      return result;
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("Failed to resend invitation"),
      };
    }
  },
};
